'use client'

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import OtpInput from 'react-otp-input'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import type { DialogProps } from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'

export const OTP_LENGTH = 6

type Feedback = 'sent' | 'send-error' | 'verify-error' | null

type OtpVerificationDialogProps = {
  open: boolean
  onClose: () => void
  onSendOtp: (resend: boolean) => Promise<boolean>
  onVerifyOtp: (otp: string) => Promise<boolean>
  onVerified: () => void
  autoSendOnOpen?: boolean
  title: string
  description: ReactNode
  sentInfo: string
  sendError: string
  verifyError: string
  resendLabel: string
  cancelLabel: string
  confirmLabel: string
  closeLabel: string
}

export function OtpVerificationDialog(props: OtpVerificationDialogProps) {
  const {
    open,
    onClose,
    onSendOtp,
    onVerifyOtp,
    onVerified,
    autoSendOnOpen = true,
    title,
    description,
    sentInfo,
    sendError,
    verifyError,
    resendLabel,
    cancelLabel,
    confirmLabel,
    closeLabel,
  } = props

  const [otp, setOtp] = useState('')
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [feedback, setFeedback] = useState<Feedback>(null)
  const sentForOpenRef = useRef(false)

  const isBusy = sending || verifying

  const send = useCallback(
    async (resend: boolean) => {
      setSending(true)
      setFeedback(null)
      const ok = await onSendOtp(resend)
      setSending(false)
      setOtp('')
      setFeedback(ok ? 'sent' : 'send-error')
    },
    [onSendOtp]
  )

  useEffect(() => {
    if (!open) {
      sentForOpenRef.current = false
      setOtp('')
      setFeedback(null)
      setSending(false)
      setVerifying(false)
      return
    }
    if (sentForOpenRef.current) return
    sentForOpenRef.current = true
    if (autoSendOnOpen) void send(false)
  }, [open, autoSendOnOpen, send])

  const handleClose = () => {
    if (isBusy) return
    onClose()
  }

  const handleDialogClose: DialogProps['onClose'] = (_event, reason) => {
    if (reason === 'backdropClick') return
    handleClose()
  }

  const handleVerify = async () => {
    if (otp.length !== OTP_LENGTH) return
    setVerifying(true)
    setFeedback(null)
    const ok = await onVerifyOtp(otp)
    setVerifying(false)
    if (ok) {
      onVerified()
      return
    }
    setOtp('')
    setFeedback('verify-error')
  }

  const error = feedback === 'send-error' ? sendError : feedback === 'verify-error' ? verifyError : null
  const info = feedback === 'sent' ? sentInfo : null

  return (
    <Dialog disableEscapeKeyDown={isBusy} fullWidth maxWidth="xs" onClose={handleDialogClose} open={open}>
      <DialogTitle sx={{ alignItems: 'center', display: 'flex', gap: 2, justifyContent: 'space-between', pr: 1.5 }}>
        <Box component="span">{title}</Box>
        <IconButton aria-label={closeLabel} disabled={isBusy} edge="end" onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          <Box display="flex" justifyContent="center">
            <OtpInput
              value={otp}
              onChange={setOtp}
              numInputs={OTP_LENGTH}
              inputType="tel"
              shouldAutoFocus
              renderInput={(inputProps) => (
                <input
                  {...inputProps}
                  style={{
                    width: 40,
                    height: 48,
                    margin: '0 4px',
                    fontSize: 22,
                    textAlign: 'center',
                    border: '1px solid rgba(0,0,0,0.23)',
                    borderRadius: 6,
                  }}
                />
              )}
            />
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {info && !error && <Alert severity="info">{info}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isBusy} onClick={() => void send(true)}>
          {resendLabel}
        </Button>
        <Button disableElevation disabled={isBusy} onClick={handleClose} variant="text">
          {cancelLabel}
        </Button>
        <LoadingButton disabled={otp.length !== OTP_LENGTH} loading={verifying} onClick={handleVerify} variant="contained">
          {confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
